import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SectionHeading } from "./SectionHeading";
import { BUSINESS, SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please call or email us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-36 bg-navy-light/40 border-t border-white/5">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <SectionHeading
              testId="contact-heading"
              eyebrow="Get in touch"
              title="Let's light up your next project"
              description="Tell us what you're building — or what you wish you had. We'll reply with a plan, not a sales pitch."
            />
            <div className="mt-10 flex flex-col gap-4">
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-call-button"
                className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-signal/60 transition-colors duration-300 group"
              >
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-signal/10 border border-signal/30 text-signal">
                  <Phone size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Call us</p>
                  <p className="font-display font-semibold text-white group-hover:text-signal transition-colors">{BUSINESS.phone}</p>
                </div>
              </a>
              <a
                href={BUSINESS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-signal/60 transition-colors duration-300 group"
              >
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-signal/10 border border-signal/30 text-signal">
                  <MessageCircle size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">WhatsApp</p>
                  <p className="font-display font-semibold text-white group-hover:text-signal transition-colors">Chat with us now</p>
                </div>
              </a>
              <a
                href={`mailto:${BUSINESS.email}`}
                data-testid="contact-email-link"
                className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-signal/60 transition-colors duration-300 group"
              >
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-signal/10 border border-signal/30 text-signal">
                  <Mail size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Email</p>
                  <p className="font-display font-semibold text-white group-hover:text-signal transition-colors">{BUSINESS.email}</p>
                </div>
              </a>
              <div data-testid="contact-address" className="glass rounded-2xl p-5 flex items-center gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-signal/10 border border-signal/30 text-signal">
                  <MapPin size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Postal address</p>
                  <p className="font-display font-semibold text-white">{BUSINESS.address}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            data-testid="enquiry-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="glass rounded-3xl p-8 md:p-10 flex flex-col gap-5"
          >
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-signal">// Send an enquiry</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="enquiry-name" className="text-sm font-medium text-muted">Name *</label>
                <Input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="enquiry-email" className="text-sm font-medium text-muted">Email *</label>
                <Input
                  id="enquiry-email"
                  type="email"
                  data-testid="enquiry-email-input"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com.au"
                  className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="enquiry-phone" className="text-sm font-medium text-muted">Phone</label>
                <Input
                  id="enquiry-phone"
                  data-testid="enquiry-phone-input"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="Optional"
                  className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-muted">Service</label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger data-testid="enquiry-service-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.title} data-testid={`enquiry-service-option-${s.id}`}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="enquiry-message" className="text-sm font-medium text-muted">Message *</label>
              <Textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your project..."
                rows={5}
                className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal resize-none"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="enquiry-submit-button"
              disabled={sending}
              className="mt-2 rounded-full bg-signal hover:bg-signal-hover text-white font-semibold py-6 text-base neon-glow transition-colors duration-200"
            >
              {sending ? (
                <span className="inline-flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Sending...</span>
              ) : (
                <span className="inline-flex items-center gap-2"><Send size={16} /> Send enquiry</span>
              )}
            </Button>
            <p className="text-xs text-muted flex items-center gap-2">
              <CheckCircle2 size={14} className="text-signal" />
              Your enquiry lands straight with the Hylyte Digital team.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
